import { omit, pick } from "lodash";
import axios from "axios";
import { apiBaseUrl, apiV3GraphQLUrl, processENV } from "../api";

import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: processENV.REACT_APP_SENTRY_DSN,
  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
  sendDefaultPii: true,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
  tracesSampleRate: 1.0,
});

const getAccessKey = () => {
  let cookie = localStorage.getItem("PB_Auth");
  if (cookie) {
    const { state } = JSON.parse(cookie);
    return state;
  }
  return "";
};

const requestConfig = () => {
  return {
    baseURL: processENV.REACT_APP_KIDS_API_URL + "/gql/v1/graphql",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
      Authorization: getAccessKey(),
    },
  };
};

const axios_client = axios.create(requestConfig());

const requestMouthscanGraphQL = async (query, variables) => {
  const response = await axios_client.post(
    apiV3GraphQLUrl,
    { query, variables },
    { headers: { Authorization: getAccessKey() } }
  );
  if (response.data.errors?.length) {
    throw new Error(response.data.errors.map((error) => error.message).join("\n"));
  }
  return response.data.data;
};

axios.interceptors.request.use(
  function (_config) {
    return requestConfig;
  },
  function (error) {
    return Promise.reject(error);
  }
);

const fields = (resource) => {
  switch (resource) {
    case "products":
    case "productpres":
      return `id description descriptionEn descriptionDe image name productId metadata tags defaultPrice currentPriceEur
      crossedPriceEur currentPriceGbp crossedPriceGbp currentPriceUsd crossedPriceUsd prodOrderTitleEn prodOrderTitleDe
      prodMarketingTitleEn prodMarketingTitleDe prodImageTitleEn prodImageTitleDe prodImageCornerNoteEn prodImageCornerNoteDe
      prodImageTooltipTextEn prodImageTooltipTextDe prodSku prodBarcode`;
    case "v2Products":
    case "v2Productpres":
      return `id productId name prodSku prodBarcode image locales tags promotionCodeIds
      translations {description cartDescription emailDescription trialDescription renewalDescription prodOrderTitle prodMarketingTitle prodImageTitle prodImageCornerNote prodImageTooltipText images}
      prices {crossedPriceExtended currentPriceExtended crossedPrice currentPrice recurringInterval recurringIntervalCount recurringTrialPeriodDays productSku}`;
    case "productTags":
      return "id category name tagId description active createdAt updatedAt";
    case "productTagCategories":
      return "id name";
    case "emailProductDetailText":
      return "id emailType tags fieldName language text";
    case "prices":
      return "id priceId type product {productId name image} unitAmount currency recurringIntervalCount recurringInterval recurringTrialPeriodDays";
    case "promotionCode":
      return "id active category couponCode createdAt currency country description modifiedAt percentage stripeCouponCodeId stripePromotionCodeId value startDate endDate productIds orderCount";
    case "challenge":
      return `challengeId name status description type successCriteria version createdAt createdBy updatedAt modifiedBy productId`;
    case "journey":
      return `journeyId name status description brushType geoffreyGoal createdAt createdBy updatedAt modifiedBy challenges { id order challengeId { id challengeId name }}`;

    case "orders":
      return `id hasTrial hasSubscription firstName lastName companyName
        trialDays stripeInvoiceIds stripeSubscriptionIds amountDiscountRate amountTotalValue amountDiscount amountShipping amountSubtotal amountTax amountTotal amountTotalAfter
        billingAddressCity billingAddressCountry billingAddressLine1 billingAddressLine2 billingAddressPostalCode billingAddressState billingName braintreeCustomerId country couponCodes couponcode createdAt currency currencySymbol discounts email hasSubscription hasTrial language modifiedAt orderId orderType paymentType shippingAddressCity shippingAddressCountry shippingAddressLine1 shippingAddressLine2 shippingAddressPostalCode shippingAddressState shippingName shippingState shippingCourierService shippingItemsBrushCount shippingItemsBrushHeadCount shippingItemsGamesSubCount shippingItemsToothPasteCount stripeCustomerId stripeInvoiceId stripeSubscriptionId stripeTaxRateId tags taxRate trialDays userId phone invoicePaymentReferenceId braintreePaypalTransactionId braintreePaypalPayerEmail stripeSetupIntent stripePaymentIntent
        orderItems {  bundleId  bundleName  currencySymbol  image  infoDelivery  infoExplainer  infoPayment  isSubscription  priceAmount  priceAmountAfterTrial  priceDefaultAmount  priceId  priceOriginalAfterTrial  productId  productName  quantity  recurringInterval  recurringIntervalCount  recurringTrialPeriodDays  tags productBarcode productNameMarketing productNameOrder productSku } `;
    case "bundlepres":
    case "bundles":
      return `id bundleId  active name nameOrderEn nameMarketingEn nameOrderDe nameMarketingDe image items itemsGbp tags bundleTags isCrossedPriceVisible
        priceAmountOneTimeCrossedEur priceAmountOneTimeCrossedGbp  priceAmountOneTimeCrossedUsd priceAmountOneTimeCurrentEur  priceAmountOneTimeCurrentGbp priceAmountOneTimeCurrentUsd  priceAmountPayAfterTrialCrossedEur priceAmountPayAfterTrialCrossedGbp  priceAmountPayAfterTrialCrossedUsd priceAmountPayAfterTrialCurrentEur  priceAmountPayAfterTrialCurrentGbp priceAmountPayAfterTrialCurrentUsd  priceAmountPayNowCrossedEur  priceAmountPayNowCrossedGbp  priceAmountPayNowCrossedUsd  priceAmountPayNowCurrentEur  priceAmountPayNowCurrentGbp  priceAmountPayNowCurrentUsd  priceAmountRecurringCrossedEur priceAmountRecurringCrossedGbp  priceAmountRecurringCrossedUsd priceAmountRecurringCurrentEur  priceAmountRecurringCurrentGbp priceAmountRecurringCurrentUsd savingsEur savingsGbp savingsUsd
        images descriptionEn descriptionDe labels {text colour lang} index imageSubscription
        itemsExtended {active currency priceId product { description image name productId defaultPrice prodSku } recurringInterval recurringIntervalCount recurringTrialPeriodDays type unitAmount unitAmountDecimal }
        itemsExtendedGbp {active currency priceId product { description image name productId defaultPrice prodSku } recurringInterval recurringIntervalCount recurringTrialPeriodDays type unitAmount unitAmountDecimal }`;
    case "v2Bundlepres":
    case "v2Bundles":
      return `id bundleId name image locales products bundleTags tags index isCrossedPriceVisible note relatedCartCrossSellBundles productDetailPageGroup
        translations {description nameOrder nameMarketing cardRecommendationTitle bulletedList images labels relatedCartCrossSellBundles productDetailPageGroup}
        prices { stripePriceIds stripePriceIdsExtended priceAmountPayNowCurrent priceAmountPayNowCrossed priceAmountPayAfterTrialCurrent priceAmountSubDifference }`;

    case "referralCodeIndividual":
      return `id userId email firstName lastName couponcode created couponValidTill vendorDbId couponText couponAmount modified couponRewardAmount bannerMessage sharedMessage `;
    case "referralCodeMessage":
      return `id createdAt updatedAt sharedMessage bannerMessage validTill amountPercents amountEur currency language`;
    case "appPuzzle":
      return `id name seasonStart seasonEnd nPieces accessLevel imageLink`;
    case "geoffreyProductRecommendations":
      return `id productId name howToInstructionUrl sizes { code color } imageLink createdAt updatedAt translations { title description productLabel productLabelBlurbs howToText benefitText sizeDescription ctaText purchaseLink } updateMessage`;
    case "users":
      return `id email oldEmail firstName lastName country  language locale isTest createdAt phoneNumber confirmed
      subscriptionStart subscriptionEnd hasPremiumAccess lastLogin countryEmoji os marketingOptIn
      marketoLeadId effectiveFeatureFlagIds featureFlagOverrideIds subscriptions {stripeCustomerId}
      statistics {loginCount hasGumPlaybrush hasGumSmartOne lastOrderDate numberOfOrders dateAppLastUsed marketoSyncAt }`;
    case "userProfiles":
      return `id userProfileId profileName lastGamePlayed gamesPlayed createdAt updatedAt
      brushingByApp {id appId sessionCount lastTimestamp appVersion}`;
    case "subscriptions":
      return `createdAt updatedAt cancelledAt provider stripeCustomerId subscriptionId status title currentPeriodStart endDate price latestPrice quantity, productType, itemTags customerEmail userId`;
    case "freeSubscriptions":
      return ` source userId userEmail subscriptionStart subscriptionEnd`;
    case "shopifyOrders":
      return `id  email status createdAt updatedAt shopifyId stripeId shopName
        customer { id stripeId email checkoutId acceptsMarketing braintreeId
          shippingAddress { firstName lastName country state city zipcode streetName houseNumber countryEmoji }
          billingAddress { firstName lastName country state city zipcode streetName houseNumber countryEmoji }
        } items { quantity id productId variantId productType title stripeProductId }`;
    case "geoffreyRecommendationTexts":
      return `id goal action geoffreyExplanationTextKey recommendationLabel recommendationExplanation completion`;
    case "orderTesters":
      return `id name emailRegexRule`;
    case "locales":
      return `id pk name country countryEmoji language currency createdAt updatedAt createdBy modifiedBy alUser alComment active supportsWebshop featureFlagIds`;
    case "allCountries":
      return `name id flag`;
    case "allLanguages":
    case "allLocales":
      return `name id`;
    case "allCurrencies":
    case "userLanguages":
      return `name id`;
    case "internalUserGroups":
      return `id name userCount localeIds createdAt updatedAt`;
    case "internalUsers":
      return `name email groupId createdAt updatedAt`;
    case "emailTemplates":
      return `id emailType description createdAt modifiedAt locales supportedLanguages updateMessage
      translations { marketoCampaignId mailchimpTemplateName subjectText previewText fromEmail fromName attachments }
      testFields {fieldName, fieldType, fieldLabel}`;
    case "emailTypes":
      return `id name`;
    case "marketoCampaigns":
      return `id name workspace computedUrl`;
    case "appRedirectLink":
      return `id createdDate linkType language value app`;
    case "cancellationCoupon":
      return `id couponId couponName tags orderNumber active couponPercentage couponAmount createdAt currency`;
    case "marketoLeadPartitions":
      return `id name`;
    case "marketoConfig":
      return `name settings`;
    case "userActivity":
      return `id email event note deviceCountry deviceOs appId createdAt`;
    case "featureFlags":
      return `id name enabled description emails localeIds createdAt updatedAt updatedBy`;
    case "pdpGroup":
      return `id pdpGroupId pageTagId url relatedPdpCrossSell country filterList filterValue bundleList`;
    case "settings":
      return `id name value createdAt updatedAt`;
    case "firebaseNotificationMessage":
      return `id title body createdAt active name language appId deepLink`;
    case "translationKeys":
      return `id namespace description valueType isRequired isRichText translations { locale value isPublished }`;
    case "mouthcheckPublicTranslations":
      return `id locale fallbackLocale version messages richTextKeys`;
    default:
      return "id";
  }
};

const skipCount = (resource) => {
  switch (resource) {
    case "allLanguages":
    case "allLocales":
    case "userLanguages":
    case "marketoConfig":
    case "marketoLeadPartitions":
    case "allCountries":
    case "allCurrencies":
    case "emailTypes":
    case "marketoCampaigns":
      return true;
    default:
      return false;
  }
};

const getListFields = (resource) => {
  switch (resource) {
    case "users":
      return `id email oldEmail firstName lastName country language createdAt phoneNumber
      subscriptionStart subscriptionEnd hasPremiumAccess lastLogin countryEmoji os`;
    case "v2Products":
    case "v2Productpres":
      return `id productId name image tags`;
    case "v2Bundles":
    case "v2Bundlepres":
      return `id bundleId name image locales products bundleTags tags index`;
    case "emailTemplates":
      return `emailType locales translationsCount languageLocales description`;
    case "locales":
      return `id pk name country countryEmoji language currency createdAt updatedAt createdBy modifiedBy alUser alComment active supportsWebshop`;
    case "geoffreyProductRecommendations":
      return `id productId name imageLink createdAt updatedAt`;
    default:
      return fields(resource);
  }
};

const getUpdateFields = (resource) => {
  switch (resource) {
    case "locales":
      return `id name country language currency supportedLanguages supportedCurrencies createdBy modifiedBy alComment featureFlagIds`;
    case "internalUserGroups":
      return `id name localeIds`;
    case "internalUsers":
      return `id email name groupId`;
    case "emailTemplates":
      return `id emailType description locales updateMessage translationsCount languageLocales
      translations { mailchimpTemplateName subjectText previewText fromEmail fromName attachments }`;
    case "productTags":
      return `category name description active`;
    case "featureFlags":
      return `description enabled emails localeIds`;
    case "geoffreyRecommendationTexts":
      return `id goal action geoffreyExplanationTextKey recommendationLabel recommendationExplanation completion`;
    case "pdpGroup":
      return `id pageTagId url relatedPdpCrossSell country filterList filterValue bundleList pdpGroupId`;
    case "translationKeys":
      return `namespace description valueType isRequired isRichText`;
    default:
      return fields(resource);
  }
};

const base64_ids = (resource) => {
  switch (resource) {
    case "emailProductDetailText":
      return true;
    case "referralCodeMessage":
      return true;
    case "appPuzzle":
      return true;
    case "appRedirectLink":
      return true;
    // case "cancellationCoupon":
    //   return true;
    case "users":
      return true;
    case "shopifyOrders":
      return true;
    case "locales":
      return true;
    case "internalUserGroups":
      return true;
    default:
      return false;
  }
};
const field_ids = (resource) => {
  switch (resource) {
    case "products":
    case "productpres":
    case "v2Products":
    case "v2Productpres":
      return "productId";
    case "bundles":
    case "bundlepres":
    case "v2Bundles":
    case "v2Bundlepres":
      return "bundleId";
    case "productTags":
      return "tagId";
    case "emailProductDetailText":
      return "id";
    case "geoffreyProductRecommendations":
      return "productId";
    case "prices":
      return "priceId";
    case "orders":
      return "orderId";
    case "internalUsers":
      return "email";
    case "emailTemplates":
      return "emailType";
    case "freeSubscriptions":
      return "userId";
    case "challenge":
      return "challengeId";
    case "journey":
      return "journeyId";
    case "pdpGroup":
      return "pdpGroupId";
    default:
      return "id";
  }
};

const sentenceCaseResource = (resource) => {
  switch (resource) {
    case "users":
      return "User:";
    case "shopifyOrders":
      return "ShopifyOrderModel:";
    case "locales":
      return "LocaleModel:";
    case "internalUserGroups":
      return "InternalUserGroupModel:";
    case "pdpGroup":
      return "ProductDetailPageGroup:";

    default:
      return resource[0].toUpperCase() + resource.substring(1) + ":";
  }
};

const pk_column_name = (resource) => {
  return "id";
};

const where_filter_custom_value = (filter, where_field) => {
  switch (where_field) {
    case "created_at__gte":
      return filter[where_field] + " 00:00:00";
    case "created_at__lte":
      return filter[where_field] + " 23:59:59";
    default:
      return filter[where_field];
  }
};

const convertToBase64 = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const custom_read_data = (resource, data) => {
  if (resource === "journey" && data && Array.isArray(data.challenges)) {
    data.challenges = data.challenges.map((c) => ({
      ...c,
      challengeId: (c.challengeId && (c.challengeId.id || c.challengeId.challengeId)) || c.challengeId || null,
    }));
  }
  return data;
};

const custom_update_data = async (resource, data) => {
  switch (resource) {
    case "geoffreyRecommendationTexts":
      return pick(data, ["goal", "action", "recommendationLabel", "recommendationExplanation"]);
    case "bundlepres":
      data = omit(data, ["product", "unitAmount"]);
      ["itemsExtended", "itemsExtendedGbp"].forEach((key) => {
        if (key in data && data[key] !== undefined) {
          data[key].forEach((item) => {
            item.product = item.product.productId;
            delete item["product.productId"];
          });
        }
      });
      return data;
    case "geoffreyProductRecommendations":
      return pick(data, ["name", "howToInstructionUrl", "sizes", "translations", "imageLink"]);
    case "journey":
      if (Array.isArray(data.challenges)) {
        data.challenges = data.challenges
          .map((c) => {
            const challengeId =
              (c.challengeId && typeof c.challengeId === "object"
                ? c.challengeId.id || c.challengeId.challengeId
                : c.challengeId) ||
              (c.challenge && (c.challenge.id || c.challenge.challengeId)) ||
              c.id ||
              null;
            if (!challengeId) return null;
            return { order: c.order, challengeId };
          })
          .filter(Boolean);
      }
      return data;
    case "locales":
    case "internalUserGroups":
    case "internalUsers":
    case "productTags":
    case "featureFlags":
      data = pick(data, getUpdateFields(resource).split(" "));
      return data;
    case "appPuzzle":
      const file = data.imageLink.rawFile;
      const base64String = await convertToBase64(file);
      data.imageLink = base64String;
      data.imageName = file.name;
    case "emailTemplates":
      data = omit(data, ["testFields"]);
      return data;
    case "pdpGroup":
      return omit(data, ["id", "pdpGroupId", "filterList", "filterValue", "bundleList"]);
    default:
      return data;
  }
};
const translationNeedsAttention = (record, locale) => {
  const translation = record.translations?.find((item) => item.locale === locale);
  const value = translation?.value;
  return (
    !translation ||
    !translation.isPublished ||
    value == null ||
    (typeof value === "string" && !value.trim()) ||
    (typeof value === "object" && !Object.keys(value).length)
  );
};

export const dataProvider = {
  getList: async (resource, { sort, pagination, filter }) => {
    if (resource === "invoices-archive") {
      return dataProvider.getInvoices();
    }
    if (resource === "creditNoteArchive") {
      return dataProvider.getCreditNotes();
    }

    if (resource === "shipping") {
      resource = "orders";
    }

    const { field, order } = sort;
    const { page, perPage } = pagination;
    if (resource === "mouthscanAnalysisResults") {
      const data = await requestMouthscanGraphQL(
        `
          query MouthscanAnalysisResults(
            $limit: Int,
            $offset: Int,
            $search: String,
            $status: String,
            $createdFrom: Date,
            $createdTo: Date,
            $sortField: String,
            $sortOrder: String
          ) {
            mouthscanAnalysisResults(
              limit: $limit,
              offset: $offset,
              search: $search,
              status: $status,
              createdFrom: $createdFrom,
              createdTo: $createdTo,
              sortField: $sortField,
              sortOrder: $sortOrder
            ) {
              id
              sessionId
              jobId
              status
              gumHealthLevel
              plaqueLevel
              whitenessScore
              stainScore
              brightnessScore
              whitenessRank
              queuedAt
              processingStartedAt
              processingCompletedAt
              algorithmProcessingTimeMs
              workerInstanceType
              analyzedAt
              failedAt
              createdAt
              scores
              session { userEmail }
            }
            mouthscanAnalysisResultsCount(
              search: $search,
              status: $status,
              createdFrom: $createdFrom,
              createdTo: $createdTo
            )
          }
        `,
        {
          limit: perPage,
          offset: (page - 1) * perPage,
          search: filter.search || null,
          status: filter.status || null,
          createdFrom: filter.createdFrom || null,
          createdTo: filter.createdTo || null,
          sortField: field,
          sortOrder: order.toLowerCase(),
        }
      );
      return {
        data: data.mouthscanAnalysisResults.map((record) => ({ ...record, id: String(record.id) })),
        total: data.mouthscanAnalysisResultsCount,
      };
    }

    if (resource === "translationKeys" && Object.values(filter).some((value) => value != null && value !== "")) {
      const countResult = await axios_client.post("", { query: "query { translationKeysCount }" });
      if (countResult.data.errors?.length) throw new Error(countResult.data.errors[0].message);
      const count = countResult.data.data.translationKeysCount;
      if (!count) return { data: [], total: 0 };
      const result = await axios_client.post("", {
        query: `query ($limit: Int, $order_by: [SortField]) {
          translationKeys(limit: $limit, orderBy: $order_by) { ${getListFields(resource)} }
        }`,
        variables: { limit: count, order_by: [{ FieldName: field, SortOrder: order.toLowerCase() }] },
      });
      if (result.data.errors?.length) throw new Error(result.data.errors[0].message);
      const query = String(filter.q ?? "")
        .trim()
        .toLowerCase();
      const locales = ["en-GB", "ja-JP"];
      const matching = result.data.data.translationKeys.filter((record) => {
        const matchesQuery =
          !query ||
          [record.id, record.namespace, record.description, ...record.translations.map((item) => item.value)].some(
            (value) =>
              String(typeof value === "object" ? JSON.stringify(value) : value ?? "")
                .toLowerCase()
                .includes(query)
          );
        const needsAttention = locales.some((locale) => translationNeedsAttention(record, locale));
        return (
          matchesQuery &&
          (!filter.translationStatus ||
            (filter.translationStatus === "needsAttention" ? needsAttention : !needsAttention)) &&
          (!filter.needsAttentionLocales || translationNeedsAttention(record, filter.needsAttentionLocales))
        );
      });
      return { data: matching.slice((page - 1) * perPage, page * perPage), total: matching.length };
    }
    return await axios_client
      .post("", {
        query: `
            query (
              $limit: Int,
              $offset: Int,
              $order_by: [SortField],
              $where: [WhereField],
            ) {
                ${resource} (limit: $limit, offset: $offset, orderBy: $order_by, where: $where)  {
                    ${getListFields(resource)}
                }

                ${skipCount(resource) ? "" : resource + "Count(where: $where)"}
            }`,
        variables: {
          limit: perPage,
          offset: (page - 1) * perPage,
          order_by: [{ FieldName: field, SortOrder: order.toLowerCase() }],
          where: Object.keys(filter).map((key) => ({
            field: key,
            value: JSON.stringify(where_filter_custom_value(filter, key)),
          })),
        },
      })
      .then((result) => ({
        data: (Array.isArray(result?.data?.data?.[resource])
          ? result.data.data[resource]
          : resource === "mouthcheckPublicTranslations" && result?.data?.data?.[resource]
          ? [result.data.data[resource]]
          : []
        ).map((res) => {
          res.id = base64_ids(resource)
            ? atob(res[field_ids(resource)]).replace(sentenceCaseResource(resource), "")
            : res[field_ids(resource)];
          return custom_read_data(resource, res);
        }),
        total: result.data.data[`${resource}Count`],
      }));
  },
  getChallengeStatusOptions: async () => {
    return await axios_client
      .post("", {
        query: `
          query ChallengeStatusOptions {
            challengeStatusOptions {
              id
              name
            }
          }
        `,
      })
      .then((result) => ({
        data: result.data.data.challengeStatusOptions,
      }));
  },
  getJourneyStatusOptions: async () => {
    return await axios_client
      .post("", {
        query: `
          query JourneyStatusOptions {
            journeyStatusOptions {
              id
              name
            }
          }
        `,
      })
      .then((result) => ({
        data: result.data.data.journeyStatusOptions,
      }));
  },
  getOne: async (resource, params) => {
    if (resource === "mouthscanAnalysisResults") {
      const data = await requestMouthscanGraphQL(
        `
          query MouthscanAnalysisResult($id: String!) {
            mouthscanAnalysisResultByPk(id: $id) {
              id
              sessionId
              jobId
              status
              gumHealthLevel
              plaqueLevel
              whitenessScore
              stainScore
              brightnessScore
              whitenessRank
              scores
              result
              oralHealthInfo
              originalImages
              recommendationLogicName
              recommendedProductIds
              recommendedAdditionalProductIds
              recommendedProducts
              recommendedAdditionalProducts
              queuedAt
              processingStartedAt
              processingCompletedAt
              algorithmProcessingTimeMs
              workerInstanceType
              analyzedAt
              failedAt
              errorMessage
              createdAt
              session {
                id
                userEmail
                deviceUuid
                deviceInfo
                formAnswersList
                formAnswerJson
                createdAt
              }
            }
          }
        `,
        { id: String(params.id) }
      );
      const record = data.mouthscanAnalysisResultByPk;
      if (!record) {
        throw new Error(`Analysis result ${params.id} was not found.`);
      }
      return { data: { ...record, id: String(record.id) } };
    }

    return await axios_client
      .post("", {
        query: `
            query ($id: String!) {
                ${resource}ByPk(id: $id) {
                    ${params.fields || fields(resource)}
                }
            }`,
        variables: {
          id: params.id,
        },
      })
      .then((result) => {
        if (result.data.data[`${resource}ByPk`] === null) {
          return { data: {} };
        }
        result.data.data[`${resource}ByPk`].id = base64_ids(resource)
          ? atob(result.data.data[`${resource}ByPk`][field_ids(resource)]).replace(sentenceCaseResource(resource), "")
          : result.data.data[`${resource}ByPk`][field_ids(resource)];
        return {
          data: custom_read_data(resource, result.data.data[`${resource}ByPk`]),
        };
      });
  },
  getMany: async (resource, params) => {
    return await axios_client
      .post("", {
        query: `
        query ($where: [WhereField]) {
                ${resource}(where: $where) {
                    ${fields(resource)}
                }
            }`,
        variables: {
          // where: params.ids.reduce(
          //   (prev, item, idx, arr) => ({
          //     // ...prev,
          //     [Object.keys(item)[0]]: { In: Object.values(item)[0] },
          //   }),
          //   {}
          // ),
          where: Object.keys(params)
            .filter((key) => key !== "meta")
            .map((key) => ({
              field: key === "ids" ? "pk__in" : key,
              // operator: "in",
              value: JSON.stringify(params[key]),
            })),
        },
      })
      .then((result) => ({
        data: (Array.isArray(result?.data?.data?.[resource]) ? result.data.data[resource] : []).map((res) => {
          res.id = base64_ids(resource)
            ? atob(res[field_ids(resource)]).replace(sentenceCaseResource(resource), "")
            : res[field_ids(resource)];
          return custom_read_data(resource, res);
        }),
      }));
  },
  getManyReference: async (resource, { target, id, sort, pagination, filter }) => {
    const { field, order } = sort;
    const { page, perPage } = pagination;
    return await axios_client
      .post("", {
        query:
          // `
          //     query ($limit: Int, $offset: Int, $order_by: [${resource}_order_by!], $where: ${
          //   resource[0].toUpperCase() + resource.substring(1)
          // }BoolExp) {
          //         ${resource}(limit: $limit, offset: $offset, order_by: $order_by, where: $where) {
          //             ${fields(resource)}
          //         }
          //         ${resource}_aggregate(where: $where) {
          //             aggregate {
          //                 count
          //             }
          //         }
          //     }
          // `
          `
            query (
              $limit: Int,
              $offset: Int,
              $order_by:${resource[0].toUpperCase() + resource.substring(1)}SortExp,
              $where: ${resource[0].toUpperCase() + resource.substring(1)}BoolExp
            ) {
                ${resource}(where: $where) {${fields(resource)}}
                ${resource}Count(where: $where)
            }
        `,
        variables: {
          limit: perPage,
          offset: (page - 1) * perPage,
          order_by: {
            sort: [{ fieldName: field, sortOrder: order.toLowerCase() }],
          },
          where: Object.keys(filter).reduce(
            (prev, key) => ({
              ...prev,
              [key]: { Eq: filter[key] },
            }),
            { [target]: { Eq: id } }
          ),
        },
      })
      .then((result) => ({
        data: result.data.data[resource],
        // total: result.data[`${resource}_aggregate`].aggregate.count,
        total: result.data.data[`${resource}_count`],
      }));
  },
  create: async (resource, params) => {
    return await axios_client
      .post("", {
        query: `
            mutation ($data: ${resource[0].toUpperCase() + resource.substring(1)}SetInput!) {
              ${resource}Create(set: $data) {
                ${resource}{
                  ${fields(resource)}
                }
              }
            }`,
        variables: {
          data: await custom_update_data(resource, omit(params.data, ["__typename"])),
        },
      })
      .then((result) => {
        if (result.data.errors && result.data.errors.length > 0) {
          throw result.data.errors[0].message;
        }
        result.data.data[`${resource}Create`][`${resource}`].id = base64_ids(resource)
          ? atob(result.data.data[`${resource}Create`][`${resource}`][field_ids(resource)]).replace(
              sentenceCaseResource(resource),
              ""
            )
          : result.data.data[`${resource}Create`][`${resource}`][field_ids(resource)];
        return {
          data: result.data.data[`${resource}Create`][`${resource}`],
        };
      });
  },
  update: async (resource, params) => {
    if (resource === "publicTranslations") {
      const { keyId, locale, value, isPublished } = params.data;
      const result = await axios_client.post("", {
        query: `
          mutation ($keyId: String!, $locale: String!, $value: GenericScalar!, $isPublished: Boolean) {
            publicTranslationUpdate(keyId: $keyId, locale: $locale, value: $value, isPublished: $isPublished) {
              translationKey { id translations { locale value isPublished } }
            }
          }`,
        variables: { keyId, locale, value, isPublished },
      });
      if (result.data.errors?.length) {
        throw new Error(result.data.errors[0].message);
      }
      const translation = result.data.data.publicTranslationUpdate.translationKey.translations.find(
        (item) => item.locale === locale
      );
      return { data: { ...translation, id: params.id, keyId } };
    }
    return await axios_client
      .post("", {
        query: `
            mutation ($id: String!, $data: ${resource[0].toUpperCase() + resource.substring(1)}SetInput!) {
                ${resource}UpdateByPk(pkColumns: { ${pk_column_name(resource)}: $id }, set: $data) {
                  ${resource}{
                    ${fields(resource)}
                  }
                }
            }`,
        variables: {
          id: params.id,
          data: await custom_update_data(resource, omit(params.data, ["__typename"])),
        },
      })
      .then((result) => {
        if (result.data.errors && result.data.errors.length > 0) {
          throw result.data.errors[0].message;
        }
        const record = result.data.data[`${resource}UpdateByPk`][`${resource}`];
        record.id = base64_ids(resource)
          ? atob(record[field_ids(resource)]).replace(sentenceCaseResource(resource), "")
          : record[field_ids(resource)];
        return {
          data: custom_read_data(resource, record),
        };
      });
  },
  updateMany: async (resource, params) => {
    if (resource === "shipping") {
      resource = "orders";
    }
    return await axios_client
      .post("", {
        query: `
            mutation ($where: [WhereField], $data: ${resource[0].toUpperCase() + resource.substring(1)}SetInput!) {
                ${resource}UpdateMany(where: $where, set: $data) {
                    affectedIds
                }
            }`,
        variables: {
          where: Object.keys(params)
            .filter((key) => key !== "meta" && key !== "data")
            .map((key) => ({
              field: key === "ids" ? "pk__in" : key,
              value: JSON.stringify(params[key]),
            })),
          data: omit(params.data, ["__typename"]),
        },
      })
      .then((result) => ({
        data: result.data.data[`${resource}UpdateMany`]["affectedIds"],
      }));
  },
  delete: async (resource, params) => {
    return await axios_client
      .post("", {
        query: `
            mutation ($id: String!) {
              ${resource}DeleteByPk(pkColumns: { ${pk_column_name(resource)}: $id }) {
                ${resource}{
                  ${fields(resource)}
                }
              }
            }`,
        variables: {
          id: params.id,
        },
      })
      .then((result) => {
        if (result.data.errors && result.data.errors.length > 0) {
          throw result.data.errors[0].message;
        }
        return {
          data: result.data.data[`${resource}DeleteByPk`]?.[`${resource}`],
        };
      });
  },
  deleteMany: async (resource, { ids }) => {
    return await axios_client
      .post("", {
        query: `
            mutation ($where: [WhereField]!) {
              ${resource}Delete(where: $where) {
                ${resource}{
                  ${fields(resource)}
                }
              }
            }`,
        variables: {
          where: [
            {
              field: "pk__in",
              value: JSON.stringify(ids),
            },
          ],
        },
      })
      .then((result) => ({
        data: result.data.data[`${resource}Delete`][`${resource}`],
      }));
  },
  getInvoices: async (resource, params) => {
    return await axios_client
      .post(apiBaseUrl, {
        query: `query InvoicesArchive {
            data {
                invoicesArchive {
                    id
                    year
                    downloadLink
                }
                total
            }
        }`,
      })
      .then((result) => ({
        data: result.data.data.data.invoicesArchive,
        total: result.data.data.data.total,
      }));
  },
  getCreditNotes: async (resource, params) => {
    return await axios_client
      .post(apiBaseUrl, {
        query: `query GetCreditNotesArchive {
          creditNotesArchiveData {
            total
            creditNotesArchive {
              id
              year
              downloadLink
            }
          }
        }`,
      })
      .then((result) => ({
        data: result.data.data.creditNotesArchiveData.creditNotesArchive,
        total: result.data.data.creditNotesArchiveData.total,
      }));
  },
};

export default dataProvider;
